import { mixedSummary, activeRows } from "./mixedDate";

/** RA tree — Scope › Building › Floor › Area › Room, built from one scope's open FRS rows.
 *  A location level is shown only when the job's rows carry a value for it; Room always shows. */
export const LEVELS = [
  { id: "building", field: "building", tag: "Bldg", word: "Building", label: (v) => (v ? v.replace(/^building\s+/i, "") : "—"),
    text: "text-indigo-700 dark:text-indigo-300" },
  { id: "floor", field: "floor", tag: "Floor", word: "Floor", label: (v) => v || "—", text: "text-violet-700 dark:text-violet-300" },
  { id: "area", field: "area", tag: "Area", word: "Area", label: (v) => v || "Unassigned", text: "text-sky-700 dark:text-sky-300" },
  { id: "room", field: "room_number", tag: "Room", word: "Room", label: (v) => v || "—", text: "text-gray-500 dark:text-gray-400" },
];
export const COVERS = { building: ["building"], floor: ["building", "floor"], area: ["building", "floor", "area"],
                        room: ["building", "floor", "area", "room_number", "room_type"] };
const natCmp = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
export const roomKey = (r) => `${r.building || ""}|${r.floor ?? ""}|${r.area || ""}|${r.room_number || ""}|${r.room_type || ""}`;
const S = (v) => String(v ?? "");

export const shownLevels = (items) => LEVELS.map((L) => L.id === "room" || items.some((r) => S(r[L.field]) !== ""));

/** The node's coordinates — only the fields its level covers, everything else blank. */
export const nodeCoords = (level, row) =>
  Object.fromEntries(["building", "floor", "area", "room_number", "room_type"].map((f) => [f, COVERS[level].includes(f) ? S(row[f]) : ""]));
export const nodeKey = (level, c) => `${level}:${COVERS[level].map((f) => c[f]).join("||")}`;
export const idSuffix = (k) => k.replace(/[^A-Za-z0-9]+/g, "-").replace(/-+$/, "");

export const whereLabel = (level, c) => [
  c.building ? `Bldg ${c.building}` : "",
  ["floor", "area", "room"].includes(level) ? `Floor ${c.floor || "—"}` : "",
  ["area", "room"].includes(level) ? (c.area ? `Area ${c.area}` : "Unassigned") : "",
  level === "room" ? `Room ${c.room_number}` : "",
].filter(Boolean).join(" · ");

/** Nested nodes: { level, value, coords, key, rows, children }. Rooms are leaves (children = null). */
export function buildTree(items, shown, li = 0) {
  if (li >= LEVELS.length) return [];
  if (!shown[li]) return buildTree(items, shown, li + 1);
  const L = LEVELS[li];
  const groups = new Map();
  for (const r of items) {
    const g = L.id === "room" ? roomKey(r) : S(r[L.field]);
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(r);
  }
  const keys = [...groups.keys()].sort((a, b) => {
    if (L.id === "area" && (a === "") !== (b === "")) return a === "" ? 1 : -1;
    return natCmp(a, b);
  });
  return keys.map((g) => {
    const rows = groups.get(g);
    const coords = nodeCoords(L.id, rows[0]);
    return { level: L.id, value: S(rows[0][L.field]), coords, key: nodeKey(L.id, coords), rows,
             rooms: new Set(rows.map(roomKey)).size,
             children: L.id === "room" ? null : buildTree(rows, shown, li + 1) };
  });
}

export const everyKey = (nodes, out = []) => {
  for (const n of nodes) { if (n.children) { out.push(n.key); everyKey(n.children, out); } }
  return out;
};

/** The lock set at exactly this node for this scope ("" level entries are the Full Job). */
export const lockAt = (levels, scopeType, level, c, field = "start") => levels.find((e) =>
  e.level === level && e.field === field && e.value && (e.scope_type || "") === scopeType
  && COVERS[level].every((f) => S(e[f]) === S(c[f])));

/** Start for a node: the lock set here, else the next live date underneath (and whether several sit below). */
export const nodeStart = (levels, scopeType, node) => {
  const e = lockAt(levels, scopeType, node.level, node.coords);
  const summary = mixedSummary(node.rows, "start");
  const live = activeRows(node.rows);
  const distinct = new Set((live.length ? live : node.rows).map((r) => r.eff_start || "").filter(Boolean));
  return { date: e ? e.value : summary.date, locked: !!e, past: !e && summary.past, multi: !e && distinct.size > 1, summary };
};

/** "Where next" under a node: the first child carrying the node's next date. */
export const nextUnder = (node, date) => {
  if (!node.children || !date) return "";
  const hit = node.children.find((ch) => (activeRows(ch.rows).length ? activeRows(ch.rows) : ch.rows).some((r) => r.eff_start === date));
  if (!hit) return "";
  const L = LEVELS.find((x) => x.id === hit.level);
  return `${L.tag} ${L.label(hit.value)}`;
};

/** Open requests and live mob cards for a node: exact ones, and the ones set above it (inherited). */
export const nodeRequests = (node, sc, openList) => {
  const requests = {}, inherited = {};
  const cover = COVERS[node.level];
  for (const kind of Object.keys(sc.requests || {})) inherited[kind] = { ...sc.requests[kind], where: "the scope" };
  for (const r of openList) {
    if (!r.level || r.scope !== sc.scope) continue;
    const rc = COVERS[r.level] || [];
    if (!rc.every((f) => S(r[f]) === S(node.coords[f]))) continue;    // not on this node's path
    if (rc.length === cover.length) requests[r.kind] = { id: r.id, at: r.at, by: r.by || "", note: r.note || "" };
    else if (rc.length < cover.length) inherited[r.kind] = { ...r, where: r.where || whereLabel(r.level, r) };
  }
  const SCHED = ["scheduled", "dispatched", "in_progress"];
  const cards = (sc.mobs || []).filter((m) => !m.punch && ["building", "floor", "area"].every((f) =>
    !S(m[f]) || !cover.includes(f) || S(m[f]) === S(node.coords[f])));
  const live = cards.filter((m) => SCHED.includes(m.status));
  const shown = live.length ? live : cards;
  const request_state = Object.fromEntries(Object.entries(requests).map(([k, r]) => [k, { state: "yellow", ...r, board: "" }]));
  if (cards.length) {
    const starts = shown.map((m) => m.start).filter(Boolean).sort();
    const ends = shown.map((m) => m.end || m.start).filter(Boolean).sort();
    request_state.mob = { state: live.length ? "green" : "yellow", board: live.length ? "dispatch" : "schedule",
                          start: starts[0] || "", end: ends[ends.length - 1] || "", crew: Math.max(0, ...shown.map((m) => +m.crew || 0)), cards };
  }
  return { requests, inherited, request_state };
};
